import { createSlice } from '@reduxjs/toolkit';
import {
  t_paymant_due_date,
  t_hospital_visit,
  t_psg_details,
  t_billing_process,
  t_cpap_instructions,
  t_simple_inspection_details,
  OtherDetail,
  hospital_visit_and_links_results,
} from 'types';
import { MedicalSearchRequest, t_medical_institution } from '../../types/medicalInstitution';
import {
  getBillingDetailsSimple,
  getBillingDetailNearest,
  getPaymentDueDateFromInvoiceMonth,
  getBillRegister,
  getItemLinkPSG,
  getItemLinkInspec,
  getFieldEditInvoice,
  getFieldHospital,
  getListOtherDetail,
} from './thunk';
import {
  checkBill,
  getBillingDetailsCPAPSpecial,
  getBillingDetailsCPAP,
  getBillingDetailsPSG,
  getCPAPByFacility,
  getHospitalStep2,
  checkHospitalAdd,
  getPastHospital,
  pageHospitalStep2,
  getBillingItems,
  createItem,
  getCreateItem,
  getBillingItemsRegister,
  registerNewBill,
  getHospitalForPage,
  getHospitalByCPAP,
  makeTrueConsultationStatus,
} from './thunk';

interface InitialState {
  items: t_medical_institution[];
  currentPage: number;
  searchValue: MedicalSearchRequest;
  hospitalCPAP: t_hospital_visit[];
  hospitalCPAPSpecial: t_hospital_visit[];
  billDetailPSG: t_psg_details[];
  billDetailSimple: t_paymant_due_date[];
  billNearest?: t_billing_process;
  totalItems: number;
  totalItemCPAPSpectial: number;
  totalItemCPAP: number;
  totalItemPSG: number;
  totalItemSimple: number;
  dataPayment?: t_paymant_due_date;
  paymentDueDate?: t_paymant_due_date;
  hospital: {
    totalItems: number;
    items: hospital_visit_and_links_results[];
  };
  psgDetails: {
    items: t_psg_details[];
  };
  itemInvoice?: t_billing_process;
  hospitalForCpap?: {
    items: t_hospital_visit[];
    totalItems: number;
  };
  optionInvoice: (t_billing_process & { value: string; name: string })[];
  hospitalsD5?: {
    items: t_hospital_visit[];
    totalItems: number;
  };
  loadStep2?: boolean;
  pastHospital?: t_hospital_visit[];
  cpapList?: t_cpap_instructions[];
  billHospital?: number | t_hospital_visit;
  itemExport?: t_billing_process;
  fieldHospital?: any[];
  itemPSGD6?: t_psg_details[];
  itemInspecD7?: t_simple_inspection_details[];
  cpapSpecialD8?: t_hospital_visit[];
  fieldEditInvoice?: any[];
  otherDetail?: OtherDetail[];
  consultationErrorStatus: boolean;
  makeTrueConsultationLoding: boolean;
  loading: boolean;
}

const initialState: InitialState = {
  items: [],
  currentPage: 1,
  searchValue: {
    facility_name: '',
    phone: '',
    code: '',
    address: '',
  },
  hospitalCPAP: [],
  hospitalCPAPSpecial: [],
  billDetailPSG: [],
  billDetailSimple: [],
  totalItems: 0,
  totalItemCPAPSpectial: 0,
  totalItemCPAP: 0,
  totalItemPSG: 0,
  totalItemSimple: 0,
  hospital: {
    items: [],
    totalItems: 0,
  },
  psgDetails: {
    items: [],
  },
  optionInvoice: [],
  consultationErrorStatus: false,
  makeTrueConsultationLoding: false,
  loading: false,
};

const billingReduces = createSlice({
  name: 'billingItems',
  initialState,
  reducers: {
    updateDataPayment: (state, action) => {
      return { ...state, dataPayment: action.payload };
    },
    updatePaymentDueDate: (state, action) => {
      return { ...state, paymentDueDate: action.payload };
    },
    updatePsgDetailsCreate: (state, action) => {
      return { ...state, psgDetails: action.payload };
    },
    resetHospitalCPAP: (state, action) => {
      return { ...state, hospitalForCpap: { items: [], totalItems: 0 } };
    },
    resetHospital: (state) => {
      return { ...state, hospital: { items: [], totalItems: 0 } };
    },
    updateHospitalSlice: (state, action) => {
      return { ...state, hospital: { items: action.payload.hospital, totalItems: action.payload.total } };
    },
    setSearchValue(state, action) {
      state.searchValue = action.payload;
    },
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    resetSearchValue(state) {
      state.searchValue = {
        code: '',
        facility_name: '',
        phone: '',
        address: '',
      };
      state.currentPage = 1;
    },
    updatePSGD6: (state, action) => {
      const newPSG = [...(state.itemPSGD6 ?? [])];
      newPSG.splice(action.payload.index, 1);
      return { ...state, itemPSGD6: newPSG };
    },
    updateInSpectionD7: (state, action) => {
      const newPSG = [...(state.itemInspecD7 ?? [])];
      newPSG.splice(action.payload.index, 1);
      return { ...state, itemInspecD7: newPSG };
    },
    updateOtherDetailSlice: (state, action) => {
      const newOtherDetail = [...(state.otherDetail ?? [])];
      newOtherDetail.splice(action.payload.index, 1);
      return { ...state, otherDetail: newOtherDetail };
    },
    resetAll: (state) => {
      return {
        items: [],
        currentPage: state.currentPage,
        searchValue: state.searchValue,
        hospitalCPAP: [],
        hospitalCPAPSpecial: [],
        billDetailPSG: [],
        billDetailSimple: [],
        totalItems: 0,
        totalItemCPAPSpectial: 0,
        totalItemCPAP: 0,
        totalItemPSG: 0,
        totalItemSimple: 0,
        hospital: {
          items: [],
          totalItems: 0,
        },
        psgDetails: {
          items: [],
        },
        optionInvoice: [],
        billNearest: undefined,
        dataPayment: undefined,
        paymentDueDate: undefined,
        itemInvoice: undefined,
        hospitalForCpap: {
          items: [],
          totalItems: 0,
        },
        hospitalsD5: {
          items: [],
          totalItems: 0,
        },
        loadStep2: false,
        pastHospital: [],
        cpapList: [],
        billHospital: undefined,
        itemExport: undefined,
        fieldHospital: [],
        itemPSGD6: [],
        itemInspecD7: [],
        cpapSpecialD8: [],
        fieldEditInvoice: [],
        otherDetail: [],
        consultationErrorStatus: false,
        makeTrueConsultationLoding: false,
        loading: false,
      };
    },
    setLoadStep2: (state, action) => ({ ...state, loadStep2: action.payload }),
    setItemInvoice: (state, action) => ({
      ...state,
      itemInvoice: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(createItem.fulfilled, (state, action) => {});
    builder.addCase(getCreateItem.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCreateItem.fulfilled, (state, action) => {
      return { ...state, dataPayment: action.payload.totalItems > 0 ? action.payload.items[0] : null, loading: false };
    });
    builder.addCase(getCreateItem.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getHospitalForPage.fulfilled, (state, action) => {
      return { ...state, hospital: action.payload.hospitals, fieldHospital: action.payload.fieldHospital };
    });
    builder.addCase(getBillingItems.fulfilled, (state, action) => {
      return { ...state, items: action.payload.items, totalItems: action.payload.totalItems };
    });
    builder.addCase(getBillingItemsRegister.fulfilled, (state, action) => {
      return { ...state, items: action.payload };
    });
    builder.addCase(getBillingDetailsCPAPSpecial.fulfilled, (state, action) => {
      return {
        ...state,
        hospitalCPAPSpecial: action.payload.items,
        totalItemCPAPSpectial: action.payload.totalItems,
      };
    });
    builder.addCase(getBillingDetailsCPAP.fulfilled, (state, action) => {
      return { ...state, hospitalCPAP: action.payload.items, totalItemCPAP: action.payload.totalItems };
    });
    builder.addCase(getBillingDetailsPSG.fulfilled, (state, action) => {
      return { ...state, billDetailPSG: action.payload, totalItemPSG: action.payload.totalItems };
    });
    builder.addCase(getBillingDetailsSimple.fulfilled, (state, action) => {
      return { ...state, billDetailSimple: action.payload, totalItemSimple: action.payload.totalItems };
    });
    builder.addCase(getBillingDetailNearest.fulfilled, (state, action) => {
      return { ...state, billNearest: action.payload };
    });
    builder.addCase(getBillRegister.fulfilled, (state, action) => {
      return { ...state, itemExport: action.payload };
    });
    builder.addCase(getPaymentDueDateFromInvoiceMonth.fulfilled, (state, action) => {
      return { ...state, paymentDueDate: action.payload ? action.payload[0] : {} };
    });
    builder.addCase(registerNewBill.fulfilled, (state, action) => {
      return { ...state, itemInvoice: action.payload.items[0] ?? null };
    });
    builder.addCase(getHospitalByCPAP.fulfilled, (state, action) => ({
      ...state,
      hospitalForCpap: {
        items: action.payload.items,
        totalItems: action.payload.totalItems,
      },
    }));
    builder.addCase(checkBill.fulfilled, (state, action) => ({ ...state, optionInvoice: action.payload }));
    builder.addCase(getHospitalStep2.fulfilled, (state, action) => ({
      ...state,
      hospitalsD5: action.payload,
      loadStep2: false,
    }));
    builder.addCase(getPastHospital.fulfilled, (state, action) => ({ ...state, pastHospital: action.payload }));
    builder.addCase(getCPAPByFacility.fulfilled, (state, action) => ({ ...state, cpapList: action.payload }));
    builder.addCase(pageHospitalStep2.fulfilled, (state, action) => ({ ...state, hospitalsD5: action.payload }));
    builder.addCase(checkHospitalAdd.fulfilled, (state, action) => ({ ...state, billHospital: action.payload }));
    builder.addCase(getItemLinkPSG.fulfilled, (state, action) => ({
      ...state,
      itemPSGD6: action.payload,
      loadStep2: false,
    }));
    builder.addCase(getItemLinkInspec.fulfilled, (state, action) => ({
      ...state,
      itemInspecD7: action.payload,
      loadStep2: false,
    }));
    builder.addCase(getFieldEditInvoice.fulfilled, (state, action) => ({ ...state, fieldEditInvoice: action.payload }));
    builder.addCase(getFieldHospital.fulfilled, (state, action) => ({ ...state, fieldHospital: action.payload }));
    builder.addCase(getListOtherDetail.fulfilled, (state, action) => ({
      ...state,
      otherDetail: action.payload,
      loadStep2: false,
    }));
    builder.addCase(makeTrueConsultationStatus.pending, (state) => ({
      ...state,
      consultationErrorStatus: false,
      makeTrueConsultationLoding: true,
    }));
    builder.addCase(makeTrueConsultationStatus.fulfilled, (state, action) => ({
      ...state,
      consultationErrorStatus: action.payload.has_error,
      makeTrueConsultationLoding: false,
    }));
    builder.addCase(makeTrueConsultationStatus.rejected, (state) => ({
      ...state,
      consultationErrorStatus: true,
      makeTrueConsultationLoding: false,
    }));
  },
});

export const {
  updateDataPayment,
  updatePaymentDueDate,
  resetHospitalCPAP,
  resetHospital,
  resetAll,
  setLoadStep2,
  updateHospitalSlice,
  updatePSGD6,
  updateInSpectionD7,
  updateOtherDetailSlice,
  setSearchValue,
  setCurrentPage,
  resetSearchValue,
  setItemInvoice,
} = billingReduces.actions;

export default billingReduces.reducer;
